import type { AccidentBenefits, AnswerCodesData } from 'api/quote-marketplace';

export type AccidentBenefitEnabledKey =
  | 'income_replacement_enabled'
  | 'catastrophic_impairment_enabled'
  | 'non_catastrophic_impairment_enabled'
  | 'caregiver_enabled'
  | 'housekeeping_home_maintenance_enabled'
  | 'death_benefit_enabled'
  | 'funeral_benefit_enabled'
  | 'dependant_care_enabled'
  | 'non_earner_enabled'
  | 'lost_educational_expenses_enabled'
  | 'expenses_of_visitors_enabled'
  | 'damage_to_personal_items_enabled'
  | 'indexation_benefit_enabled';

export type AccidentBenefitLimitKey =
  | 'income_replacement_limit_per_week_cad'
  | 'catastrophic_impairment_limit_cad'
  | 'non_catastrophic_impairment_limit_cad'
  | 'caregiver_limit_cad'
  | 'housekeeping_home_maintenance_limit_cad'
  | 'death_benefit_limit_cad'
  | 'funeral_benefit_limit_cad'
  | 'dependant_care_limit_cad'
  | 'non_earner_limit_cad'
  | 'lost_educational_expenses_limit_cad'
  | 'expenses_of_visitors_limit_cad'
  | 'damage_to_personal_items_limit_cad';

export interface AccidentBenefitDefinition {
  enabledKey: AccidentBenefitEnabledKey;
  limitKey?: AccidentBenefitLimitKey;
  answerCodeField?: keyof AnswerCodesData;
  coverageOptionsField?:
    | 'caregiver_coverage_options'
    | 'housekeeping_home_maintenance_coverage_options';
  name: string;
  description: string;
  category: 'standard' | 'optional';
  isRecommended: boolean;
  limitType: 'choice' | 'number' | 'optional_number' | 'none';
  limitOptional?: boolean;
}

export const ACCIDENT_BENEFIT_DEFINITIONS: AccidentBenefitDefinition[] = [
  {
    enabledKey: 'income_replacement_enabled',
    limitKey: 'income_replacement_limit_per_week_cad',
    answerCodeField: 'income_replacement_limit_per_week_cad',
    name: 'Income Replacement Benefit',
    description:
      'Provides weekly income payments if you are unable to work due to injuries from an automobile accident. Optional coverage allows you to increase the weekly maximum payable above the standard amount.',
    category: 'optional',
    isRecommended: true,
    limitType: 'choice',
  },
  {
    enabledKey: 'catastrophic_impairment_enabled',
    limitKey: 'catastrophic_impairment_limit_cad',
    answerCodeField: 'catastrophic_impairment_limit_cad',
    name: 'Medical Rehabilitation - Catastrophic Impairment',
    description:
      'Provides additional coverage for medical, rehabilitation, and attendant care expenses if you sustain a catastrophic impairment as a result of an automobile accident.',
    category: 'optional',
    isRecommended: true,
    limitType: 'choice',
  },
  {
    enabledKey: 'non_catastrophic_impairment_enabled',
    limitKey: 'non_catastrophic_impairment_limit_cad',
    answerCodeField: 'non_catastrophic_impairment_limit_cad',
    name: 'Medical Rehabilitation - Non-Catastrophic Impairment',
    description:
      'Provides additional coverage for medical, rehabilitation, and attendant care expenses for non-catastrophic impairments.',
    category: 'optional',
    isRecommended: true,
    limitType: 'choice',
  },
  {
    enabledKey: 'caregiver_enabled',
    limitKey: 'caregiver_limit_cad',
    coverageOptionsField: 'caregiver_coverage_options',
    name: 'Caregiver Benefit',
    description:
      'Provides a weekly benefit if you are injured and unable to provide care to dependents.',
    category: 'optional',
    isRecommended: true,
    limitType: 'optional_number',
    limitOptional: true,
  },
  {
    enabledKey: 'housekeeping_home_maintenance_enabled',
    limitKey: 'housekeeping_home_maintenance_limit_cad',
    coverageOptionsField: 'housekeeping_home_maintenance_coverage_options',
    name: 'Housekeeping & Home Maintenance Benefit',
    description:
      'Provides a weekly benefit if you are injured and unable to perform housekeeping and home maintenance tasks.',
    category: 'optional',
    isRecommended: true,
    limitType: 'optional_number',
    limitOptional: true,
  },
  {
    enabledKey: 'death_benefit_enabled',
    limitKey: 'death_benefit_limit_cad',
    name: 'Death Benefit',
    description:
      'Provides a lump-sum payment to eligible surviving family members if death results from injuries sustained in an automobile accident.',
    category: 'optional',
    isRecommended: true,
    limitType: 'optional_number',
    limitOptional: true,
  },
  {
    enabledKey: 'funeral_benefit_enabled',
    limitKey: 'funeral_benefit_limit_cad',
    name: 'Funeral Benefit',
    description:
      'Covers reasonable funeral expenses if death results from an automobile accident.',
    category: 'optional',
    isRecommended: true,
    limitType: 'optional_number',
    limitOptional: true,
  },
  {
    enabledKey: 'dependant_care_enabled',
    limitKey: 'dependant_care_limit_cad',
    name: 'Dependant Care Benefit',
    description:
      'Provides a weekly benefit to assist with the cost of caring for dependent children if injuries prevent you from providing care yourself.',
    category: 'optional',
    isRecommended: true,
    limitType: 'optional_number',
    limitOptional: true,
  },
  {
    enabledKey: 'non_earner_enabled',
    limitKey: 'non_earner_limit_cad',
    name: 'Non-Earner Benefit',
    description:
      'Provides a weekly benefit if you were not employed at the time of the accident and your injuries prevent you from continuing your normal life activities.',
    category: 'optional',
    isRecommended: true,
    limitType: 'optional_number',
    limitOptional: true,
  },
  {
    enabledKey: 'lost_educational_expenses_enabled',
    limitKey: 'lost_educational_expenses_limit_cad',
    name: 'Lost Educational Expenses',
    description:
      'Covers certain education-related expenses, such as tuition and books, if injuries prevent you from completing a school term.',
    category: 'optional',
    isRecommended: true,
    limitType: 'optional_number',
    limitOptional: true,
  },
  {
    enabledKey: 'expenses_of_visitors_enabled',
    limitKey: 'expenses_of_visitors_limit_cad',
    name: 'Expenses of Visitors',
    description:
      'Covers reasonable expenses for family members or close relatives to visit you while you are receiving medical treatment as a result of an automobile accident.',
    category: 'optional',
    isRecommended: true,
    limitType: 'optional_number',
    limitOptional: true,
  },
  {
    enabledKey: 'damage_to_personal_items_enabled',
    limitKey: 'damage_to_personal_items_limit_cad',
    name: 'Damage to Personal Items  (clothing, glasses, hearing aids, etc.)',
    description:
      'Covers repair or replacement of personal items (such as clothing or eyeglasses) damaged in an automobile accident.',
    category: 'optional',
    isRecommended: true,
    limitType: 'optional_number',
    limitOptional: true,
  },
  {
    enabledKey: 'indexation_benefit_enabled',
    name: 'Indexation Benefit',
    description:
      'Increases certain Accident Benefit limits over time to help protect against inflation.',
    category: 'optional',
    isRecommended: true,
    limitType: 'none',
  },
];

export const STANDARD_BENEFITS = ACCIDENT_BENEFIT_DEFINITIONS.filter(
  (b) => b.category === 'standard',
);

export const OPTIONAL_BENEFITS = ACCIDENT_BENEFIT_DEFINITIONS.filter(
  (b) => b.category === 'optional',
);

export const RECOMMENDED_BENEFITS = ACCIDENT_BENEFIT_DEFINITIONS.filter(
  (b) => b.isRecommended,
);

export const createEmptyAccidentBenefits = (): AccidentBenefits => ({
  income_replacement_enabled: false,
  income_replacement_limit_per_week_cad: '',
  income_replacement_limit_per_week_cad_other: null,
  catastrophic_impairment_enabled: false,
  catastrophic_impairment_limit_cad: '',
  non_catastrophic_impairment_enabled: false,
  non_catastrophic_impairment_limit_cad: '',
  caregiver_enabled: false,
  caregiver_limit_cad: null,
  caregiver_coverage_options: [],
  housekeeping_home_maintenance_enabled: false,
  housekeeping_home_maintenance_limit_cad: null,
  housekeeping_home_maintenance_coverage_options: [],
  death_benefit_enabled: false,
  death_benefit_limit_cad: null,
  funeral_benefit_enabled: false,
  funeral_benefit_limit_cad: null,
  dependant_care_enabled: false,
  dependant_care_limit_cad: null,
  non_earner_enabled: false,
  non_earner_limit_cad: null,
  lost_educational_expenses_enabled: false,
  lost_educational_expenses_limit_cad: null,
  expenses_of_visitors_enabled: false,
  expenses_of_visitors_limit_cad: null,
  damage_to_personal_items_enabled: false,
  damage_to_personal_items_limit_cad: null,
  indexation_benefit_enabled: false,
});

export const areAllBenefitsConfigured = (
  benefits: AccidentBenefits,
): boolean => {
  for (const def of ACCIDENT_BENEFIT_DEFINITIONS) {
    if (def.coverageOptionsField && benefits[def.enabledKey]) {
      if (!benefits[def.coverageOptionsField]?.length) return false;
    }
  }
  return true;
};

export const getDisabledRecommendedBenefits = (
  benefits: AccidentBenefits,
): AccidentBenefitDefinition[] => {
  return RECOMMENDED_BENEFITS.filter((def) => !benefits[def.enabledKey]);
};
